import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { BaseWidgetComponent } from '../base-widget.component';
import { DashboardItemTypeValueDisplay } from '../../models/DashboardItemTypeValueDisplay';
import { BehaviorSubject, debounceTime, interval, takeUntil, takeWhile } from 'rxjs';
import { DashboardService } from '../../services/dashboard.service';
import { LocaleSessionService } from '../../../../shared/services/localeSession.service';
import { SingleDataItemDto, SingleDataResponseDto } from '../../../../../api-main';
import {
  getModeString,
  ValueDisplayMode,
} from '../../widgets-settings/dashboard-value-display-settings/ValueDisplayMode.model';
import { getRangeFromDataDisplayDefaultRanges } from '../../../../shared/utils/getRangeFromDataDisplayDefaultRanges';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SingleValueRequestService } from '../../services/single-value-request.service';
import { FitTextDirective } from '../../../../shared/directives/fit-text.directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AsyncPipe } from '@angular/common';
import { FormatValuePipe } from '../../../../shared/pipes/format-value.pipe';
import { DateDisplayComponent } from '../../../../shared/components/date-display/date-display.component';
import { DataDisplayDefaultRanges } from '../../../../shared/models/DataDisplayDefaultRanges';
import { cfgUnits, getCfgUnitIdByUnit } from '../../../../shared/obis-code/CfgUnits';

@Component({
  selector: 'app-dashboard-value-display',
  templateUrl: './dashboard-value-display.component.html',
  styleUrls: ['./dashboard-value-display.component.scss'],
  standalone: true,
  imports: [
    MatProgressBarModule,
    FitTextDirective,
    AsyncPipe,
    TranslateModule,
    FormatValuePipe,
    DateDisplayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardValueDisplayComponent extends BaseWidgetComponent implements OnInit {
  localeSession = inject(LocaleSessionService);
  private translate = inject(TranslateService);
  protected override dashboardService: DashboardService;
  protected override cd: ChangeDetectorRef;
  private singleValueService = inject(SingleValueRequestService);

  protected override detach = false;

  constructor() {
    const dashboardService = inject(DashboardService);
    const cd = inject(ChangeDetectorRef);

    super();

    this.dashboardService = dashboardService;
    this.cd = cd;
  }

  public value: number;

  public date: Date;

  public unit: string;

  savedResponse: SingleDataResponseDto;

  private errorOccurred = false;

  loading = new BehaviorSubject(false);

  dateFrom = new BehaviorSubject(null);

  dateTo = new BehaviorSubject(null);

  @Input() dataInput: DashboardItemTypeValueDisplay;

  override get data(): DashboardItemTypeValueDisplay {
    if (this.dataInput) return this.dataInput;
    return this.item?.data?.valueDisplay ?? ({} as DashboardItemTypeValueDisplay);
  }

  get editMode(): boolean {
    return this.dashboardService.editMode$.value;
  }

  get isDataValid(): boolean {
    return this.data.loggerId != null && this.data.deviceId != null && this.data.valueId != null;
  }

  ngOnInit() {
    this.observeInterval();
    this.detectChangesOnLoading();
    this.observeData();
    this.dashboardService.editMode$.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value) this.watchData();
    });
    this.requestData();
  }

  get valueString(): number {
    const value = this.value;
    const baseUnit = getCfgUnitIdByUnit(this.unit);
    const unit = cfgUnits.get(this.data.cfg_unit);
    if (!unit || !baseUnit) {
      if (!this.errorOccurred) console.error('No unit found');
      this.errorOccurred = true;
      return value;
    }
    if (unit.factor === 0 || baseUnit?.factor === 0) {
      if (!this.errorOccurred) console.error('Incorrect unit factor');
      this.errorOccurred = true;
      return value;
    }
    if (!value || !unit?.factor) return 0;
    return (value / unit.factor) * baseUnit.factor;
  }

  get header() {
    if (this.data.header && this.data.header != '') return this.data.header;
    return this.savedResponse?.description != null
      ? `${this.savedResponse?.description} (${this.translate.instant(
          getModeString(this.data.mode),
        )})`
      : '';
  }

  requestData() {
    if (!this.isDataValid) return;
    this.loading.next(true);
    this.singleValueService.sendRequest(this.getRequest(), this.item.id);
  }

  private observeInterval() {
    this.singleValueService.refresh$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.requestData());
  }

  private observeData() {
    this.singleValueService
      .getResponse$(this.item.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((response) => {
        if (response != null) this.updateValues(response);
        this.loading.next(false);
        this.itemLoaded();
      });
  }

  private detectChangesOnLoading() {
    this.loading
      .pipe(takeUntil(this.unsubscribe), debounceTime(100))
      .subscribe(() => this.detectChanges());
  }

  private getRequest(): SingleDataItemDto {
    this.setupRange();
    return {
      logger_id: this.data.loggerId,
      value_id: this.data.valueId,
      range_start: this.data.range.min / 1e3,
      range_end: this.data.range.max / 1e3,
      mode: ValueDisplayMode[this.data.mode].toLowerCase() as any,
      consumption: this.data.consumption,
    };
  }

  private setupRange() {
    if (this.data.rangeMode == null) return;

    if (this.data.rangeMode !== DataDisplayDefaultRanges.CUSTOM) {
      const range = getRangeFromDataDisplayDefaultRanges(this.data.rangeMode);
      this.data.range = Object.assign({}, range);
      this.data.range.min *= 1e3;
      this.data.range.max *= 1e3;
      this.dateFrom.next(this.data.range.min);
      this.dateTo.next(this.data.range.max);
    } else {
      const range = this.data.range;
      this.data.range = Object.assign({}, range);
      this.dateFrom.next(this.data.range.min);
      this.dateTo.next(this.data.range.max);
    }
    this.cd.markForCheck();
  }

  private watchData() {
    let requestSettings = this.requestSettingsKey();
    let displaySettings = this.displaySettingsKey();
    interval(300)
      .pipe(takeUntil(this.unsubscribe))
      .pipe(takeWhile(() => this.editMode, true))
      .subscribe(() => {
        if (this.data.valueId == null) {
          this.value = null;
          return;
        }
        if (requestSettings !== this.requestSettingsKey()) {
          requestSettings = this.requestSettingsKey();
          displaySettings = this.displaySettingsKey();
          this.setupRange();
          this.requestData();
        } else if (displaySettings !== this.displaySettingsKey()) {
          displaySettings = this.displaySettingsKey();
          this.detectChanges();
        }
      });
  }

  // Settings used in the request - changing them needs new data.
  private requestSettingsKey(): string {
    const { valueId, mode, rangeMode, consumption, range } = this.data;
    // A relative range is recomputed from "now" by setupRange(), so only a custom one is compared.
    const custom = rangeMode === DataDisplayDefaultRanges.CUSTOM ? [range?.min, range?.max] : [];
    return JSON.stringify([valueId, mode, rangeMode, consumption, ...custom]);
  }

  // Settings not depending on the request - changing them needs a render.
  private displaySettingsKey(): string {
    const { showHeader, header, showUnit, unit, cfg_unit, showDate, decimalPlaces } = this.data;
    return JSON.stringify([showHeader, header, showUnit, unit, cfg_unit, showDate, decimalPlaces]);
  }

  private updateValues(value: SingleDataResponseDto) {
    this.savedResponse = value;
    this.savedResponse.start *= 1000;
    this.savedResponse.end *= 1000;
    const val = value.value != null ? Number(value.value) : null;
    this.value = !isNaN(val) ? val : null;
    this.unit = value.unit && value.unit.toLowerCase() != 'none' ? value.unit : '';
    this.date = value.time ? new Date(value.time) : null;
    this.dateFrom.next(value.start);
    this.dateTo.next(value.end);
    this.cd.detectChanges();
  }
}
